import { IVariantPersonaDiscountItem } from "@/api_framework/api_modals/Inventory";
import { useVariantPersonaDiscountsAPI } from "@/app/hooks/api_hooks/Inventory/useVariantPersonaDiscountsAPI";
import { useVariantPersonaDiscountsUpdateAPI } from "@/app/hooks/api_hooks/Inventory/useVariantPersonaDiscountsUpdateAPI";
import { useCouponUserPerSonaListAPI } from "@/app/hooks/api_hooks/coupon/useCouponUserPerSonaListAPI";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select, { MultiValue } from "react-select";
import { FiArrowLeft, FiPlus, FiTrash2 } from "react-icons/fi";
import clsx from "clsx";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";

type PersonaOption = { value: string; label: string };

interface UIRule {
  persona_names: string[];
  discount: number;
}

interface EditableRow {
  vetic_variant_id: string;
  variant_title: string;
  item_name: string;
  is_child_variant: boolean;
  child_variant_pack_size: number | null;
  discount_rate: number;
  visible_on_app: boolean;
  rules: UIRule[];
}

// Group existing {persona_name, discount} entries by discount → one multi-persona rule per discount.
const toEditableRow = (variant: IVariantPersonaDiscountItem): EditableRow => {
  const byDiscount = new Map<number, string[]>();
  (variant.persona_discount_rules ?? []).forEach((rule) => {
    const list = byDiscount.get(rule.discount) ?? [];
    if (rule.persona_name) list.push(rule.persona_name);
    byDiscount.set(rule.discount, list);
  });
  const rules: UIRule[] = Array.from(byDiscount.entries()).map(([discount, persona_names]) => ({
    discount,
    persona_names,
  }));
  return {
    vetic_variant_id: variant.vetic_variant_id,
    variant_title: variant.variant_title,
    item_name: variant.item_name,
    is_child_variant: variant.is_child_variant,
    child_variant_pack_size: variant.child_variant_pack_size,
    discount_rate: variant.discount_rate ?? 0,
    visible_on_app: variant.visible_on_app,
    rules,
  };
};

const toInt = (value: string) => {
  const parsed = value === "" ? 0 : parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const selectStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  control: (base: any) => ({ ...base, minHeight: 36, fontSize: 13, borderColor: "#d1d5db" }),
  menu: (base: any) => ({ ...base, fontSize: 13 }),
};

const ProductPersonaDiscountComponent = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const productId = params.get("product_id");
  const { execute: fetchVariants, variants, productName } = useVariantPersonaDiscountsAPI();
  const updatePersonaDiscounts = useVariantPersonaDiscountsUpdateAPI();
  const { execute: fetchUserPersonas, userpersona } = useCouponUserPerSonaListAPI();
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [saving, setSaving] = useState(false);

  const personaOptions: PersonaOption[] = useMemo(
    () => (userpersona ?? []).map((persona) => ({ value: persona, label: persona })),
    [userpersona],
  );

  // Personas that appear in more than one rule of the same variant (would collapse to last-wins on save).
  const duplicatePersonasByRow = useMemo(
    () =>
      rows.map((row) => {
        const seen = new Set<string>();
        const duplicates = new Set<string>();
        row.rules.forEach((rule) => {
          new Set(rule.persona_names).forEach((persona) => {
            if (seen.has(persona)) duplicates.add(persona);
            else seen.add(persona);
          });
        });
        return duplicates;
      }),
    [rows],
  );

  const variantLabel = (row: EditableRow) =>
    row.is_child_variant ? `Pack of ${row.child_variant_pack_size}` : row.variant_title || "Base variant";

  const outOfRange = (value: number) => value < 0 || value > 50;

  const validate = (): string | null => {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const label = variantLabel(row);
      if (outOfRange(row.discount_rate)) {
        return `Default discount for “${label}” must be between 0 and 50%.`;
      }
      if (row.rules.some((rule) => outOfRange(rule.discount))) {
        return `A persona discount for “${label}” must be between 0 and 50%.`;
      }
      if (duplicatePersonasByRow[i].size > 0) {
        return `“${label}”: ${Array.from(duplicatePersonasByRow[i]).join(", ")} appears in more than one rule. Keep each persona in a single rule.`;
      }
    }
    return null;
  };

  useEffect(() => {
    if (productId) fetchVariants(productId);
  }, [productId]);

  useEffect(() => {
    fetchUserPersonas();
  }, [fetchUserPersonas]);

  useEffect(() => {
    setRows(variants.map(toEditableRow));
  }, [variants]);

  const mutateRow = (variantIdx: number, updater: (row: EditableRow) => EditableRow) =>
    setRows((prev) => prev.map((row, i) => (i === variantIdx ? updater(row) : row)));

  const addRule = (variantIdx: number) =>
    mutateRow(variantIdx, (row) => ({ ...row, rules: [...row.rules, { persona_names: [], discount: 0 }] }));

  const removeRule = (variantIdx: number, ruleIdx: number) =>
    mutateRow(variantIdx, (row) => ({ ...row, rules: row.rules.filter((_, j) => j !== ruleIdx) }));

  const updateRulePersonas = (variantIdx: number, ruleIdx: number, personaNames: string[]) =>
    mutateRow(variantIdx, (row) => ({
      ...row,
      rules: row.rules.map((rule, j) => (j === ruleIdx ? { ...rule, persona_names: personaNames } : rule)),
    }));

  const updateRuleDiscount = (variantIdx: number, ruleIdx: number, value: string) =>
    mutateRow(variantIdx, (row) => ({
      ...row,
      rules: row.rules.map((rule, j) => (j === ruleIdx ? { ...rule, discount: toInt(value) } : rule)),
    }));

  const updateDefaultDiscount = (variantIdx: number, value: string) =>
    mutateRow(variantIdx, (row) => ({ ...row, discount_rate: toInt(value) }));

  const handleSave = async () => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    const updates = rows.map((row) => {
      // Expand multi-persona rules → flat {persona_name, discount}; last write wins per persona.
      const perPersona = new Map<string, number>();
      row.rules.forEach((rule) => {
        rule.persona_names.forEach((name) => {
          const trimmed = name?.trim();
          if (trimmed) perPersona.set(trimmed, Number(rule.discount) || 0);
        });
      });
      return {
        vetic_variant_id: row.vetic_variant_id,
        discount_rate: Number(row.discount_rate) || 0,
        persona_discount_rules: Array.from(perPersona.entries()).map(([persona_name, discount]) => ({
          persona_name,
          discount,
        })),
      };
    });
    setSaving(true);
    const res = await updatePersonaDiscounts({ updates });
    setSaving(false);
    if (res.status) {
      toast.success("Persona discounts saved");
      if (productId) fetchVariants(productId);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex flex-row items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            className={clsx(SECONDARY_BUTTON, "shrink-0")}
            onClick={() => navigate("/app/inventory/product-list")}
          >
            <FiArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="hidden sm:block h-5 w-px bg-gray-300" />
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base font-semibold text-gray-900 whitespace-nowrap">Persona Discounts</h1>
            {productName ? <p className="text-xs text-gray-500 truncate">{productName}</p> : null}
          </div>
        </div>
        <button
          type="button"
          className={clsx(PRIMARY_BUTTON, "shrink-0 disabled:opacity-50 disabled:cursor-not-allowed")}
          onClick={handleSave}
          disabled={saving || rows.length === 0}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="border border-gray-200 bg-white rounded-lg p-8 text-center text-gray-500 text-sm">
          No variants found for this product.
        </div>
      ) : (
        <div className="rounded-xl bg-[#F5F5F5] px-[3px] pb-[3px] overflow-hidden">
          {/* column header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-xs font-medium text-[#1F1F23]">
            <div className="col-span-3">Product / Variant</div>
            <div className="col-span-2">Default Discount</div>
            <div className="col-span-1">Visible</div>
            <div className="col-span-6 flex items-center gap-2">
              <div className="flex-1 min-w-0">Persona Discounts</div>
              <div className="w-[86px] shrink-0">Discount</div>
              <div className="w-6 shrink-0" />
            </div>
          </div>

          <div className="flex flex-col gap-0.5">
            {rows.map((row, variantIdx) => {
              const isFirst = variantIdx === 0;
              const isLast = variantIdx === rows.length - 1;
              const isSingle = rows.length === 1;

              return (
                <div
                  key={row.vetic_variant_id}
                  className={clsx(
                    "grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-4 items-start bg-white",
                    isSingle && "rounded-xl",
                    isFirst && !isSingle && "rounded-t-xl",
                    isLast && !isSingle && "rounded-b-xl",
                  )}
                >
                  {/* Product / Variant */}
                  <div className="md:col-span-3">
                    <p className="text-xs font-medium text-gray-900">{productName || "-"}</p>
                    <p className="text-xs text-[#6C727F] mt-0.5">
                      {row.is_child_variant
                        ? `Pack of ${row.child_variant_pack_size}`
                        : row.variant_title || "Base variant"}
                    </p>
                  </div>

                  {/* Default discount */}
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-1">
                      <input
                        className={`text-sm border rounded-md px-2 py-1.5 w-20 focus:ring-1 focus:ring-indigo-500 outline-none ${
                          outOfRange(row.discount_rate) ? "border-red-400" : "border-gray-300"
                        }`}
                        type="number"
                        min={0}
                        max={50}
                        value={`${row.discount_rate ?? 0}`}
                        onChange={(e) => updateDefaultDiscount(variantIdx, e.target.value)}
                      />
                      <span className="text-gray-500 text-sm">%</span>
                    </div>
                  </div>

                  {/* Visible */}
                  <div className="md:col-span-1">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        row.visible_on_app ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {row.visible_on_app ? "Yes" : "No"}
                    </span>
                  </div>

                  {/* Persona rules */}
                  <div className="md:col-span-6">
                    <div className="flex flex-col gap-2">
                      {row.rules.length === 0 && (
                        <p className="text-xs text-gray-400">No rules — default discount applies to everyone.</p>
                      )}
                      {row.rules.map((rule, ruleIdx) => {
                        const duplicates = rule.persona_names.filter((name) =>
                          duplicatePersonasByRow[variantIdx]?.has(name),
                        );
                        return (
                          <div key={ruleIdx} className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 min-w-0">
                                <Select<PersonaOption, true>
                                  isMulti
                                  options={personaOptions}
                                  value={rule.persona_names.map((name) => ({ value: name, label: name }))}
                                  onChange={(opts: MultiValue<PersonaOption>) =>
                                    updateRulePersonas(
                                      variantIdx,
                                      ruleIdx,
                                      opts.map((option) => option.value),
                                    )
                                  }
                                  placeholder="Search personas…"
                                  isSearchable
                                  menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                                  styles={selectStyles}
                                />
                              </div>
                              <div className="flex items-center gap-1 shrink-0 w-[86px]">
                                <input
                                  className={`text-sm border rounded-md px-2 py-1.5 w-16 focus:ring-1 focus:ring-indigo-500 outline-none ${
                                    outOfRange(rule.discount) ? "border-red-400" : "border-gray-300"
                                  }`}
                                  type="number"
                                  min={0}
                                  max={50}
                                  placeholder="%"
                                  value={`${rule.discount ?? 0}`}
                                  onChange={(e) => updateRuleDiscount(variantIdx, ruleIdx, e.target.value)}
                                />
                                <span className="text-gray-500 text-sm">%</span>
                              </div>
                              <button
                                type="button"
                                className="text-gray-400 hover:text-red-500 shrink-0 p-1"
                                onClick={() => removeRule(variantIdx, ruleIdx)}
                                title="Remove rule"
                                aria-label="Remove rule"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                            {duplicates.length > 0 && (
                              <p className="text-xs text-red-500">
                                {duplicates.join(", ")} also used in another rule — keep each persona in one rule.
                              </p>
                            )}
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        className="flex items-center gap-1 text-black underline hover:opacity-70 text-xs font-medium w-fit mt-1"
                        onClick={() => addRule(variantIdx)}
                      >
                        <FiPlus className="w-3.5 h-3.5" /> Add persona rule
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPersonaDiscountComponent;
